import { ref } from 'vue'
import type { CheckResult, ViteAppConfiguration } from '../types'

/**
 * Composable for HTTP status checking functionality
 * Handles API calls to WordPress REST API for URL checking
 */
export function useHttpChecker() {
  const isChecking = ref(false)
  const results = ref<CheckResult[]>([])
  const error = ref<string | null>(null)

  /**
   * Check HTTP status for multiple URLs
   * 
   * @param urls - Array of URLs to check
   * @param followRedirects - Whether to follow redirects (default: true)
   * @param maxRedirects - Maximum number of redirects to follow (default: 10)
   * @param timeout - Request timeout in seconds (default: 30)
   */
  const checkUrls = async (
    urls: string[],
    followRedirects: boolean = true,
    maxRedirects: number = 10,
    timeout: number = 30
  ) => {
    isChecking.value = true
    error.value = null
    results.value = []

    try {
      // Get WordPress configuration from global
      const config = (window as any).conflatedDomainsConfig as ViteAppConfiguration

      if (!config || !config.restUrl || !config.nonce) {
        throw new Error('WordPress REST API configuration not found')
      }

      const response = await fetch(`${config.restUrl}/${config.apiNamespace}/tools/http-check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': config.nonce
        },
        body: JSON.stringify({
          urls: urls,
          follow_redirects: followRedirects,
          max_redirects: maxRedirects,
          timeout: timeout
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('API Error Response:', errorText)
        throw new Error(`API request failed: ${response.status} ${response.statusText}`)
      }

      const data = await response.json()

      // WordPress REST API wraps response in {success: true, data: {...}}
      const responseData = data.data || data

      if (!responseData.success) {
        throw new Error(responseData.error || 'HTTP check failed')
      }

      results.value = responseData.results || []

    } catch (e) {
      error.value = e instanceof Error ? e.message : 'An unknown error occurred'
      console.error('HTTP check error:', e)
    } finally {
      isChecking.value = false
    }
  }

  /**
   * Clear check results and errors
   */
  const clear = () => {
    results.value = []
    error.value = null
    isChecking.value = false
  }

  /**
   * Export results as CSV
   * @param resultsToExport - Optional results array to export (uses internal results if not provided)
   */
  const exportCSV = (resultsToExport?: CheckResult[]) => {
    const dataToExport = resultsToExport || results.value
    if (dataToExport.length === 0) return null

    const headers = ['URL', 'Status Code', 'Response Time (s)', 'Final URL', 'Redirect Chain', 'Error']
    const rows = dataToExport.map(result => [
      result.url,
      result.status_code.toString(),
      result.response_time.toFixed(3),
      result.final_url,
      result.redirect_chain.map(step => `${step.url} (${step.status})`).join(' → '),
      result.error || ''
    ])

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n')

    return csvContent
  }

  /**
   * Export results as JSON
   * @param resultsToExport - Optional results array to export (uses internal results if not provided)
   */
  const exportJSON = (resultsToExport?: CheckResult[]) => {
    const dataToExport = resultsToExport || results.value
    return JSON.stringify(dataToExport, null, 2)
  }

  return {
    isChecking,
    results,
    error,
    checkUrls,
    clear,
    exportCSV,
    exportJSON
  }
}
